import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import { object, reach } from 'yup';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { PERIOD_COUNT_FIELDS } from '../../../types/NeglectedOrDelinquentPeriodCountFields';
import { PanelType } from '../../../types/PanelType';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import { isValidNeglectedOrDelinquentCount } from '../../../utilities/neglectedOrDelinquentUtilities';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const NeglectedOrDelinquentPostSecondaryEducationPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    const education = values.postSecondaryEducation;
    const canCalculate = PERIOD_COUNT_FIELDS.every(({ key }) =>
        isValidNeglectedOrDelinquentCount(education[key]));
    const total = canCalculate
        ? PERIOD_COUNT_FIELDS.reduce((sum, { key }) => sum + Number(education[key]), 0)
        : '';

    return (
        <Panel panelType={PanelType.INFO} heading="Post-Secondary Education">
            <section>
                <p>
                    Provide the <strong>number of students</strong> who were accepted
                    and/or enrolled in a post-secondary program:
                </p>
                <div className="nord-post-secondary">
                    {PERIOD_COUNT_FIELDS.map(({ key, label }) => {
                        const field = `postSecondaryEducation.${key}`;
                        const inputSchema = object().shape({
                            [field]: reach(validationSchema, field),
                        });
                        const warning = key === 'whileInProgram'
                            ? errors.postSecondaryWhileLimit
                            : errors.postSecondaryExitLimit;
                        return (
                            <div key={key}>
                                <FormTextbox
                                    field={field}
                                    displayName={label}
                                    disabled={disabled}
                                    validationSchema={inputSchema}
                                />
                                {typeof warning === 'string' && (
                                    <p className="nord-demographics-error" role="alert">
                                        {warning}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    <div className="field-container disabled">
                        <FormInputLabel
                            field="totalStudentsPostSecondary"
                            displayName="Total Students Post-Secondary"
                            disabled
                        />
                        <input
                            id="totalStudentsPostSecondary"
                            className="data-input"
                            type="text"
                            disabled
                            readOnly
                            value={total}
                        />
                    </div>
                </div>
            </section>
        </Panel>
    );
};

export default NeglectedOrDelinquentPostSecondaryEducationPanel;
